import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from "@/context/AuthContext";
import { capitalizeWords } from '@/helper';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { authUser, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => setIsMenuOpen(false), [location]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const isActive = (path: string) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const avatarLetter = authUser?.name?.[0]?.toUpperCase() || "U";

  // Dynamic classes for scroll state
  const headerClass = `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || !isHomePage
    ? 'bg-white/90 backdrop-blur-md shadow-sm'
    : 'bg-transparent'
    }`;

  // Helper for text colors - white when transparent, default (black/dark) when scrolled
  const linkTextColor = (scrolled || !isHomePage) ? '' : '!text-white hover:!text-white after:!bg-white';
  const iconColor = (scrolled || !isHomePage) ? 'text-foreground' : 'text-white';

  const getLinkClass = (path: string) =>
    `header-link ${isActive(path) ? 'active' : ''} ${linkTextColor}`;

  return (
    <header className={headerClass}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex p-4 items-center justify-between relative">
          {/* Logo */}
          <Link to="/" className="flex items-center" aria-label="PropVerse Home">
            <img
              src="/image/logo.png"
              alt="PropVerse Logo"
              className="h-8 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 absolute left-1/2 transform -translate-x-1/2">
            <Link to="/" className={getLinkClass('/')}>Home</Link>
            <Link to="/properties" className={getLinkClass('/properties')}>Properties</Link>
            <DropdownMenu>
              <DropdownMenuTrigger className={`header-link flex items-center ${['/developers', '/locations', '/market-insights'].some(path => location.pathname.startsWith(path)) ? 'active' : ''
                } ${linkTextColor}`}>
                Explore <ChevronDown className="ml-1 h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 glass-card">
                <DropdownMenuItem asChild><Link to="/developers">Developers</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/locations">Locations</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/market-insights">Market Insights</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/blogs" className={getLinkClass('/blogs')}>Blog</Link>
            <Link
              to="/mortgage-calculator"
              className={getLinkClass('/mortgage-calculator')}
            >
              Mortgage Calculator
            </Link>
          </nav>

          {/* Desktop User Menu — guest vs logged in */}
          <div className="hidden md:flex items-center space-x-4">
            {!authUser ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className={`rounded-full w-10 h-10 p-0 btn-gold bg-gold hover:bg-gold/90 text-white`} aria-label="User Menu">
                      <User className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="glass-card w-56">
                    <DropdownMenuItem asChild><Link to="/broker/login">Broker Login</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/login">Admin Login</Link></DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button asChild className="btn-gold">
                  <Link to="/contact">Get In Touch</Link>
                </Button>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className={`w-auto px-3 flex items-center gap-2 ${iconColor} hover:bg-white/10`} aria-label="Account Settings">
                    <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center font-semibold">
                      {avatarLetter}
                    </div>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="glass-card w-64 p-2 space-y-1">
                  <div className="px-3 py-2">
                    <p className="font-semibold">{capitalizeWords(authUser.name)}</p>
                    <p className="text-xs text-muted-foreground">{authUser.email}</p>
                  </div>

                  {/* Dashboard Link */}
                  <DropdownMenuItem
                    onClick={() => {
                      if (authUser?.userType === "ADMIN") navigate("/admin/dashboard");
                      else navigate("/broker/dashboard");
                    }}
                    className="cursor-pointer font-medium"
                  >
                    Dashboard
                  </DropdownMenuItem>

                  {/* Logout */}
                  <DropdownMenuItem
                    className="text-red-600 font-medium cursor-pointer"
                    onClick={handleLogout}
                  >
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button className={`md:hidden ${iconColor}`} onClick={toggleMenu} aria-label="Toggle mobile menu">
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className={`md:hidden ${isMenuOpen ? 'block' : 'hidden'} glass-card-strong p-4 rounded-none h-screen`}>
        <nav className="flex flex-col space-y-4 p-5">
          <Link to="/" className="text-lg font-medium">Home</Link>
          <Link to="/properties" className="text-lg font-medium">Properties</Link>
          <Link to="/developers" className="text-lg font-medium">Developers</Link>
          <Link to="/locations" className="text-lg font-medium">Locations</Link>
          <Link to="/market-insights" className="text-lg font-medium">Market Insights</Link>
          <Link to="/blogs" className="text-lg font-medium">Blog</Link>
          <Link to="/mortgage-calculator" className="text-lg font-medium">Mortgage Calculator</Link>

          <hr className="border-t border-border" />

          {!authUser ? (
            <>
              <Link to="/broker/login" className="text-lg font-medium">Broker Login</Link>
              <Link to="/admin/login" className="text-lg font-medium">Admin Login</Link>
              <Button asChild className="btn-gold w-full mt-2">
                <Link to="/contact">Get In Touch</Link>
              </Button>
            </>
          ) : (
            <>
              <p className="font-semibold">{capitalizeWords(authUser.name)}</p>
              <p className="text-sm text-muted-foreground -mt-2 mb-4">{authUser.email}</p>

              {/* Dashboard */}
              <Button
                className="w-full mb-2"
                onClick={() => {
                  if (authUser?.userType === "ADMIN") navigate("/admin/dashboard");
                  else navigate("/broker/dashboard");
                }}
              >
                Dashboard
              </Button>

              {/* Logout */}
              <Button className="w-full" variant="destructive" onClick={handleLogout}>
                Logout
              </Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
